<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { p1Api } from '@/lib/p1-api';
import { acceptedMime, sha256Hex } from '@/lib/p2-api';
import {
  p4Api,
  type PaymentAttemptRecord,
  type PaymentCorrectionRecord,
  type PaymentTaskRecord,
} from '@/lib/p4-api';

const router = useRouter();
const userId = ref('');
const canInitiate = ref(false);
const canReview = ref(false);
const tasks = ref<PaymentTaskRecord[]>([]);
const corrections = ref<PaymentCorrectionRecord[]>([]);
const selectedAttemptId = ref('');
const reason = ref('');
const evidenceFileId = ref('');
const evidenceName = ref('');
const evidenceStatus = ref('');
const reviewComments = ref<Record<string, string>>({});
const loading = ref(true);
const submitting = ref(false);
const error = ref('');
const message = ref('');
const tab = ref<'REQUEST' | 'REVIEW' | 'HISTORY'>('REQUEST');

const successfulAttempts = computed(() => {
  const guarded = new Set(
    corrections.value
      .filter((item) => item.status !== 'REJECTED')
      .map((item) => item.paymentAttemptId),
  );
  return tasks.value.flatMap((task) =>
    task.status === 'SUCCEEDED'
      ? task.attempts
          .filter((attempt) => attempt.status === 'SUCCEEDED' && !guarded.has(attempt.id))
          .map((attempt) => ({ task, attempt }))
      : [],
  );
});
const selected = computed(() =>
  successfulAttempts.value.find((item) => item.attempt.id === selectedAttemptId.value),
);
const reviewQueue = computed(() =>
  corrections.value.filter((item) => item.status === 'PENDING_REVIEW'),
);
const visibleCorrections = computed(() =>
  tab.value === 'REVIEW' ? reviewQueue.value : corrections.value,
);

onMounted(load);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const me = await p1Api.me();
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    userId.value = me.userId;
    canInitiate.value = me.permissions.includes('payment:correction:initiate');
    canReview.value = me.permissions.includes('payment:correction:review');
    if (!canInitiate.value && !canReview.value) {
      error.value = '当前账号没有付款更正权限';
      return;
    }
    const [correctionRows, taskRows] = await Promise.all([
      p4Api.paymentCorrections(),
      canInitiate.value ? p4Api.tasks() : Promise.resolve([]),
    ]);
    corrections.value = correctionRows;
    tasks.value = taskRows;
    if (!successfulAttempts.value.some((item) => item.attempt.id === selectedAttemptId.value))
      selectedAttemptId.value = '';
  } catch (cause) {
    const detail = cause instanceof Error ? cause.message : '付款更正工作台加载失败';
    if (detail.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = detail;
  } finally {
    loading.value = false;
  }
}

function selectAttempt(attempt: PaymentAttemptRecord) {
  selectedAttemptId.value = attempt.id;
  evidenceFileId.value = '';
  evidenceName.value = '';
  evidenceStatus.value = '';
  reason.value = '';
  error.value = '';
  message.value = '';
}

async function uploadEvidence(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file || !selected.value) return;
  const mimeType = acceptedMime(file);
  if (!mimeType || !['application/pdf', 'image/jpeg', 'image/png'].includes(mimeType)) {
    error.value = '更正证据仅支持 PDF、JPG 或 PNG';
    return;
  }
  submitting.value = true;
  error.value = '';
  try {
    const intent = await p4Api.createCorrectionEvidenceUploadIntent({
      paymentAttemptId: selected.value.attempt.id,
      originalName: file.name,
      mimeType,
      sizeBytes: file.size,
    });
    const uploaded = await fetch(intent.uploadUrl, {
      method: 'PUT',
      headers: intent.requiredHeaders,
      body: file,
    });
    if (!uploaded.ok) throw new Error('对象存储上传失败');
    await p4Api.completeUpload(intent.id, await sha256Hex(file));
    evidenceFileId.value = intent.id;
    evidenceName.value = file.name;
    evidenceStatus.value = 'PENDING_SCAN';
    message.value = '证据已上传，正在进行安全扫描';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '更正证据上传失败';
  } finally {
    submitting.value = false;
  }
}

async function refreshEvidence() {
  if (!evidenceFileId.value) return;
  try {
    const file = await p4Api.file(evidenceFileId.value);
    evidenceStatus.value = file.status;
    message.value =
      file.status === 'CLEAN' ? '证据安全扫描通过，可以提交更正申请' : '证据仍在扫描或未通过';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '证据状态刷新失败';
  }
}

async function createCorrection() {
  if (!selected.value || evidenceStatus.value !== 'CLEAN' || reason.value.trim().length < 2) {
    error.value = '请填写更正原因，并等待更正证据安全扫描通过';
    return;
  }
  await runMutation(async () => {
    await p4Api.createPaymentCorrection(selected.value!.attempt.id, {
      attemptVersion: selected.value!.attempt.version,
      taskVersion: selected.value!.task.version,
      evidenceFileId: evidenceFileId.value,
      reason: reason.value.trim(),
    });
    message.value = '付款更正申请已提交，等待另一名总部财务人员复核';
    selectedAttemptId.value = '';
  });
}

async function approve(item: PaymentCorrectionRecord) {
  const comment = reviewComments.value[item.id]?.trim();
  await runMutation(async () => {
    await p4Api.approvePaymentCorrection(item.id, {
      version: item.version,
      ...(comment ? { comment } : {}),
    });
    message.value = '付款更正已复核通过，原记录已标记更正并生成冲销留痕';
  });
}

async function reject(item: PaymentCorrectionRecord) {
  const reviewReason = reviewComments.value[item.id]?.trim() ?? '';
  if (reviewReason.length < 2) {
    error.value = '驳回必须填写至少 2 个字的原因';
    return;
  }
  await runMutation(async () => {
    await p4Api.rejectPaymentCorrection(item.id, { version: item.version, reason: reviewReason });
    message.value = '付款更正申请已驳回，原付款事实未发生变化';
  });
}

async function openEvidence(fileId: string) {
  try {
    const result = await p4Api.fileDownloadUrl(fileId);
    window.open(result.url, '_blank', 'noopener,noreferrer');
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '更正证据打开失败';
  }
}

async function runMutation(action: () => Promise<void>) {
  submitting.value = true;
  error.value = '';
  message.value = '';
  try {
    await action();
    await load();
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '付款更正操作失败';
  } finally {
    submitting.value = false;
  }
}

function money(value: string) {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(
    Number(value),
  );
}
function dateTime(value: string | null) {
  return value
    ? new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    : '—';
}
</script>

<template>
  <main class="workspace-shell payment-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>付款更正</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink to="/documents">我的单据</RouterLink>
        <RouterLink to="/approval-tasks">审批待办</RouterLink>
        <RouterLink to="/payment-tasks">付款处理</RouterLink>
        <RouterLink class="active" to="/payment-corrections">付款更正</RouterLink>
        <RouterLink to="/repayment-collections">还款确认</RouterLink>
        <RouterLink to="/loan-offsets">借款抵扣</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/login">切换账号</RouterLink>
    </header>

    <section class="page-heading">
      <div>
        <p class="eyebrow">P4 · 财务双人复核</p>
        <h1>付款更正工作台</h1>
        <p>原付款记录不可覆盖；发起人提交证据后，必须由另一名总部财务人员复核。</p>
      </div>
      <button class="quiet-button" type="button" :disabled="loading" @click="load">刷新</button>
    </section>

    <div class="approval-tabs payment-tabs" role="tablist" aria-label="付款更正视图">
      <button
        v-if="canInitiate"
        type="button"
        :class="{ active: tab === 'REQUEST' }"
        @click="tab = 'REQUEST'"
      >
        发起更正
      </button>
      <button
        v-if="canReview"
        type="button"
        :class="{ active: tab === 'REVIEW' }"
        @click="tab = 'REVIEW'"
      >
        待我复核（{{ reviewQueue.length }}）
      </button>
      <button type="button" :class="{ active: tab === 'HISTORY' }" @click="tab = 'HISTORY'">
        全部记录
      </button>
    </div>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <p v-if="message" class="inline-alert" role="status">{{ message }}</p>
    <section v-if="loading" class="document-empty">正在加载付款更正记录…</section>

    <section v-else-if="tab === 'REQUEST' && canInitiate" class="payment-layout">
      <div v-if="successfulAttempts.length" class="approval-list payment-list">
        <button
          v-for="item in successfulAttempts"
          :key="item.attempt.id"
          type="button"
          class="approval-row payment-row"
          :class="{ selected: selectedAttemptId === item.attempt.id }"
          @click="selectAttempt(item.attempt)"
        >
          <span class="document-icon">改</span>
          <span class="document-main">
            <span class="document-title"
              ><strong>{{ item.task.paymentNo }}</strong
              ><span class="status-pill" data-status="SUCCEEDED">已付款</span></span
            >
            <span
              >{{ item.task.sourceDocument.documentNo }} · 流水号
              {{ item.attempt.bankTransactionNo }}</span
            >
            <small>{{ dateTime(item.attempt.paidAt) }}</small>
          </span>
          <strong class="document-amount">{{ money(item.task.amount) }}</strong>
        </button>
      </div>
      <div v-else class="document-empty">
        <strong>没有可发起更正的付款</strong>
        <p>仅已付款且不存在有效更正流程的记录可以申请。</p>
      </div>

      <aside v-if="selected" class="form-section payment-operation-panel">
        <div class="section-heading-row">
          <div>
            <p class="eyebrow">{{ selected.task.paymentNo }}</p>
            <h2>提交更正证据</h2>
          </div>
        </div>
        <dl class="payment-facts">
          <div>
            <dt>来源单据</dt>
            <dd>{{ selected.task.sourceDocument.documentNo }}</dd>
          </div>
          <div>
            <dt>原付款金额</dt>
            <dd>{{ money(selected.task.amount) }}</dd>
          </div>
          <div>
            <dt>原银行流水</dt>
            <dd>{{ selected.attempt.bankTransactionNo }}</dd>
          </div>
        </dl>
        <form class="payment-form" @submit.prevent="createCorrection">
          <label
            ><span>更正原因</span
            ><textarea v-model.trim="reason" maxlength="1000" rows="4" required />
          </label>
          <label class="upload-button payment-upload"
            >{{ submitting ? '处理中…' : '上传更正证据'
            }}<input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              :disabled="submitting"
              @change="uploadEvidence"
          /></label>
          <div v-if="evidenceFileId" class="receipt-state">
            <span>{{ evidenceName }} · {{ evidenceStatus }}</span
            ><button class="text-button" type="button" @click="refreshEvidence">
              刷新扫描状态
            </button>
          </div>
          <button class="primary-button" type="submit" :disabled="submitting">提交更正申请</button>
        </form>
      </aside>
    </section>

    <section v-else class="approval-list correction-list">
      <article
        v-for="item in visibleCorrections"
        :key="item.id"
        class="form-section correction-card"
      >
        <div class="section-heading-row">
          <div>
            <p class="eyebrow">{{ item.paymentAttempt.paymentTask.paymentNo }}</p>
            <h2>{{ item.paymentAttempt.paymentTask.sourceDocument.documentNo }}</h2>
          </div>
          <span class="status-pill" :data-status="item.status">{{ item.status }}</span>
        </div>
        <dl class="payment-facts">
          <div>
            <dt>更正金额</dt>
            <dd>{{ money(item.amount) }}</dd>
          </div>
          <div>
            <dt>发起人</dt>
            <dd>{{ item.requestedBy.name }}</dd>
          </div>
          <div>
            <dt>发起时间</dt>
            <dd>{{ dateTime(item.requestedAt) }}</dd>
          </div>
          <div>
            <dt>证据</dt>
            <dd>
              <button
                v-if="item.files[0]"
                class="text-button"
                type="button"
                @click="openEvidence(item.files[0].file.id)"
              >
                {{ item.files[0].file.originalName }}
              </button>
              <span v-else>—</span>
            </dd>
          </div>
        </dl>
        <p>{{ item.reason }}</p>
        <p v-if="item.reviewedBy" class="section-hint">
          {{ item.reviewedBy.name }} · {{ dateTime(item.reviewedAt) }} · {{ item.reviewComment }}
        </p>
        <div v-if="tab === 'REVIEW' && item.requestedByUserId === userId" class="inline-alert">
          你是本申请发起人，必须由另一名总部财务人员复核。
        </div>
        <div v-else-if="tab === 'REVIEW' && canReview" class="payment-form">
          <label
            ><span>复核意见（驳回时必填）</span
            ><textarea v-model="reviewComments[item.id]" maxlength="1000" rows="3" />
          </label>
          <div class="correction-actions">
            <button
              class="primary-button"
              type="button"
              :disabled="submitting"
              @click="approve(item)"
            >
              复核通过</button
            ><button
              class="danger-button"
              type="button"
              :disabled="submitting"
              @click="reject(item)"
            >
              驳回申请
            </button>
          </div>
        </div>
      </article>
      <div v-if="!visibleCorrections.length" class="document-empty">
        <strong>暂无付款更正记录</strong>
      </div>
    </section>
  </main>
</template>
